import { useEffect, useMemo, useRef } from 'react';

/**
 * A custom hook that debounces a function.
 * @param {Function} callback The function to debounce.
 * @param {number} delay The debounce delay in milliseconds.
 * @returns A memoized version of the callback that is debounced.
 */
export function useDebounce(callback, delay) {
  const callbackRef = useRef(callback);

  // Update ref to the latest callback.
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Create the debounced function.
  const debouncedCallback = useMemo(
    () => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          callbackRef.current(...args);
        }, delay);
      };
    },
    [delay]
  );

  return debouncedCallback;
}
